/**
 * Delivery clock (THE-1444): the real clock against the planned one, while the agenda is being run.
 *
 * Pure and non-durable. The caller owns the two instants — `startedAtMs` (the facilitator pressed
 * start) and, optionally, when the CURRENT block really began — and passes `nowMs` in, so nothing
 * here reads a clock and every case is testable. Nothing is stored: a reload loses the run, by design
 * (persisting the real times would be its own story in the spec).
 *
 * Two ways to know which block is current:
 *  - `current` given and found in the agenda: that block, with its own real start. This is what lets
 *    the facilitator be AHEAD (moved on early) or skip a block.
 *  - otherwise the plan decides: the block whose planned window holds `now`. This is also the
 *    fallback when the current block was removed mid-run, so a removal never breaks the blocks after it.
 *
 * Minutes are WHOLE minutes truncated toward zero, so a 40 s slip is "on time", never "over 1 min".
 */
import type { AgendaBlockInput, ScheduledBlock } from "./types.js";
import { recomputeAgenda } from "./agenda.js";

const MIN_MS = 60_000;

export interface DeliveryInput {
  /** Real start of the agenda, epoch ms. */
  startedAtMs: number;
  nowMs: number;
  /** The block the facilitator moved to, and when it really began. */
  current?: { blockId: string; startedAtMs: number } | null;
}

export type DeliveryPhase = "notStarted" | "running" | "done";
export type DeliveryState = "ahead" | "onTime" | "over";

export interface DeliveryClock {
  phase: DeliveryPhase;
  /** The block on air; `null` when not started or done. */
  block: ScheduledBlock | null;
  /** Position of `block` in the agenda (0-based), -1 when none. */
  index: number;
  blockElapsedMin: number;
  /** Minutes left in the block, never negative. */
  blockRemainingMin: number;
  /** Minutes past the block's planned length, never negative. */
  blockOverrunMin: number;
  /** Positive = behind the plan, negative = ahead. Projected end vs planned end. */
  agendaDeltaMin: number;
  agendaOverrunMin: number;
  agendaAheadMin: number;
  state: DeliveryState;
}

const whole = (ms: number): number => (ms < 0 ? Math.ceil(ms / MIN_MS) : Math.floor(ms / MIN_MS)) || 0;

function stateOf(deltaMin: number): DeliveryState {
  return deltaMin > 0 ? "over" : deltaMin < 0 ? "ahead" : "onTime";
}

function build(
  phase: DeliveryPhase,
  block: ScheduledBlock | null,
  index: number,
  elapsedMs: number,
  deltaMs: number,
): DeliveryClock {
  const dur = block ? block.durationMin * MIN_MS : 0;
  const delta = whole(deltaMs);
  return {
    phase,
    block,
    index,
    blockElapsedMin: block ? whole(Math.max(0, elapsedMs)) : 0,
    blockRemainingMin: block ? Math.max(0, Math.ceil((dur - elapsedMs) / MIN_MS)) : 0,
    blockOverrunMin: block ? Math.max(0, whole(elapsedMs - dur)) : 0,
    agendaDeltaMin: delta,
    agendaOverrunMin: Math.max(0, delta),
    agendaAheadMin: Math.max(0, -delta),
    state: stateOf(delta),
  };
}

export function deliveryClock(blocks: readonly AgendaBlockInput[], input: DeliveryInput): DeliveryClock {
  const { startedAtMs, nowMs } = input;
  const plan = recomputeAgenda(blocks);
  if (nowMs < startedAtMs || plan.blocks.length === 0) return build("notStarted", null, -1, 0, 0);

  const elapsedAgendaMs = nowMs - startedAtMs;
  const picked = input.current ? plan.blocks.findIndex((b) => b.id === input.current?.blockId) : -1;

  if (picked >= 0 && input.current) {
    const block = plan.blocks[picked]!;
    const blockElapsed = Math.max(0, nowMs - input.current.startedAtMs);
    // Projected end = now + what is left of this block + every block after it; the plan's end is
    // start + total. Zero-length blocks add nothing either way.
    const leftInBlock = Math.max(0, block.durationMin * MIN_MS - blockElapsed);
    const after = plan.totalMin - (block.startOffsetMin + block.durationMin);
    const projectedEnd = nowMs + leftInBlock + after * MIN_MS;
    return build("running", block, picked, blockElapsed, projectedEnd - (startedAtMs + plan.totalMin * MIN_MS));
  }

  // Plan-driven: the block whose window holds `now`; zero-length blocks are never "on air".
  const idx = plan.blocks.findIndex(
    (b) => b.durationMin > 0 && elapsedAgendaMs < (b.startOffsetMin + b.durationMin) * MIN_MS,
  );
  if (idx < 0) {
    // Past the planned end with nobody holding a block: the run is over. (A facilitator still on a
    // block, behind schedule, goes through the `current` branch above and keeps counting the overrun.)
    return build("done", null, -1, 0, 0);
  }
  const block = plan.blocks[idx]!;
  return build("running", block, idx, elapsedAgendaMs - block.startOffsetMin * MIN_MS, 0);
}
